import { ReactNode } from 'react';
import { Link as FluentLink, Tree, TreeItem, TreeItemLayout, TreeProps } from '@fluentui/react-components';

import { TDisplayItem } from '../ItemList';

export type TSidebarTreeItem = {
  id: string;
  label: ReactNode;
  href?: string;
  target?: string;
  rel?: string;
  onClick?: () => void;
  renderLabel?: () => ReactNode;
};

export type TSidebarTreeSection = {
  id: string;
  label: ReactNode;
  items: TSidebarTreeItem[];
  hideWhenEmpty?: boolean;
};

type TProps = {
  sections: TSidebarTreeSection[];
  ariaLabel: string;
  emptyLabel?: string;
  className?: string;
  treeProps?: Partial<TreeProps>;
};

const SidebarTree = ({ sections, ariaLabel, emptyLabel = 'Nothing found.', className, treeProps }: TProps) => {
  const visibleSections = sections.filter((s) => !(s.hideWhenEmpty && s.items.length === 0));
  if (visibleSections.length === 0) return <p className="pl-3 text-xs text-neutral-600">{emptyLabel}</p>;

  return (
    <Tree size="small" aria-label={ariaLabel} className={className} {...treeProps}>
      {visibleSections.map((section) => (
        <TreeItem itemType="branch" key={section.id} value={section.id}>
          <TreeItemLayout>{section.label}</TreeItemLayout>
          <Tree>
            {section.items.map((item) => (
              <TreeItem itemType="leaf" key={item.id} onClick={item.onClick} value={item.id}>
                {/* We deliberately style external links differently to indicate they open in a new tab. */}
                <TreeItemLayout>
                  {item.renderLabel ? (
                    item.renderLabel()
                  ) : item.href ? (
                    <FluentLink href={item.href} target={item.target} rel={item.rel}>
                      {item.label}
                    </FluentLink>
                  ) : (
                    <span className="text-brand-70 hover:!text-brand-60">{item.label}</span>
                  )}
                </TreeItemLayout>
              </TreeItem>
            ))}
          </Tree>
        </TreeItem>
      ))}
    </Tree>
  );
};

/** Build tree sections from items grouped by version, iterating versions in order. */
export const buildVersionGroupedSections = <T extends { _id: string; name: string }>(
  versions: { id: string }[],
  itemsByVersion: Record<string, T[]>,
  buildHref: (item: T) => string,
  buildLabel?: (item: T) => ReactNode
): TSidebarTreeSection[] => {
  return versions.reduce<TSidebarTreeSection[]>((acc, version) => {
    const items = itemsByVersion[version.id];
    if (!items || items.length === 0) return acc;

    acc.push({
      id: version.id,
      label: `v${version.id}`,
      items: items.map((item) => ({
        id: item._id,
        label: buildLabel ? buildLabel(item) : item.name,
        href: buildHref(item),
        target: '_blank',
        rel: 'noopener noreferrer',
      })),
    });

    return acc;
  }, []);
};

/** Convert display items to sidebar tree items, showing a "Nothing found." placeholder when empty. */
export const buildTreeItems = (items: TDisplayItem[], sectionId: string): TSidebarTreeItem[] => {
  if (items.length === 0)
    return [
      {
        id: `${sectionId}-empty`,
        label: 'Nothing found.',
        renderLabel: () => <span className="text-xs text-neutral-600">Nothing found.</span>,
      },
    ];

  return items.map((item) => ({
    id: item.id,
    label: item.text,
    href: item.href,
    target: item.href ? '_blank' : undefined,
    rel: item.href ? 'noopener noreferrer' : undefined,
    onClick: item.onClick,
  }));
};

export default SidebarTree;
